import { React } from 'Imports';
import { useState, useEffect } from 'react';
import SingleSelect from '$Components/FilterComponents/Filters/SingleSelect';
import VPButton from '$Components/Shared/Button/VPButton';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { VideoEventResponse } from '$Generated/api';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { isEncompass } from '$Utilities/integrationPlatform';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';

const styles = require('./ScoreVideoEvent.scss') as {
    container: string;
    select: string;
};

interface ScoreVideoEventProps {
    videoEvent: VideoEventResponse;
    userCanEdit: boolean;
    onVideoEventUpdated: () => void;
    onDirtyStateChange?: (isDirty: boolean) => void;
}

const scoreOptions: IDropdownItem[] = [
    { label: '-1 (Low Risk)', value: -1 },
    { label: '-2 (Moderate Risk)', value: -2 },
    { label: '-3 (High Risk)', value: -3 },
    { label: '-4 (Critical)', value: -4 },
    { label: '+1', value: 1 },
    { label: '+2', value: 2 },
    { label: '+3', value: 3 },
];

const _ScoreVideoEvent = (props: ScoreVideoEventProps & IVideoEventServiceInjectedProps) => {
    const { videoEvent, userCanEdit, onVideoEventUpdated, onDirtyStateChange, videoEvents } = props;
    const { showSnackbarMessage, handleCloseSnackbar } = useAppSnackbar();

    const [savedScore, setSavedScore] = useState<number | null | undefined>(videoEvent.score);
    const [selectedScore, setSelectedScore] = useState<IDropdownItem | null>(
        videoEvent.score !== undefined && videoEvent.score !== null
            ? (scoreOptions.find((o) => o.value === videoEvent.score) ?? null)
            : null,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const liveStatus = videoEvents.getState().selectedVideoEventResult.data?.videoEventStatus ?? videoEvent.videoEventStatus;
    const isStatusLocked = liveStatus === 'Dismissed' || liveStatus === 'Completed';
    const isDisabled = !userCanEdit || isStatusLocked;

    const isClearMode = selectedScore !== null && selectedScore?.value === savedScore;
    const hasUnsavedScore = selectedScore !== null && !isClearMode;

    useEffect(() => {
        if (liveStatus == 'Dismissed') {
            setSelectedScore(null);
            setSavedScore(null);
        }
    }, [liveStatus]);

    useEffect(() => {
        onDirtyStateChange?.(hasUnsavedScore);
    }, [hasUnsavedScore]);

    const handleSave = async () => {
        if (!selectedScore || isSubmitting) return;
        setIsSubmitting(true);

        try {
            await videoEvents.postVideoEventRatings({ videoEventIdList: [videoEvent.id!], ratingValue: selectedScore.value });
            showSnackbarMessage({
                message: 'Event scored successfully.',
                alertType: AlertTypes.Success,
                isEncompass: isEncompass,
                showIcon: isEncompass,
                onClose: handleCloseSnackbar,
            });
            setSavedScore(selectedScore.value);
            videoEvents.updateLocalVideoEventScore(selectedScore.value, videoEvent.id!);
            if (videoEvent.videoEventStatus === 'New') {
                videoEvents.updateLocalVideoEventStatus('In_Progress', videoEvent.id!);
            }
            onVideoEventUpdated();
        } catch {
            showSnackbarMessage({
                message: 'Unable to score event.',
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
                onClose: handleCloseSnackbar,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClear = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            await videoEvents.postVideoEventRatings({ videoEventIdList: [videoEvent.id!], ratingValue: 0 });
            showSnackbarMessage({
                message: 'Event score cleared successfully.',
                alertType: AlertTypes.Success,
                isEncompass: isEncompass,
                showIcon: isEncompass,
                onClose: handleCloseSnackbar,
            });
            setSavedScore(null);
            setSelectedScore(null);
            videoEvents.updateLocalVideoEventScore(undefined, videoEvent.id!);
            if (videoEvent.videoEventStatus === 'New') {
                videoEvents.updateLocalVideoEventStatus('In_Progress', videoEvent.id!);
            }
            onVideoEventUpdated();
        } catch {
            showSnackbarMessage({
                message: 'Unable to clear event score.',
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
                onClose: handleCloseSnackbar,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.select}>
                <SingleSelect
                    placeholder="Select a score ..."
                    label=""
                    hideLabel={true}
                    items={scoreOptions}
                    disabled={isDisabled}
                    selectionChanged={(item) => setSelectedScore(item)}
                    selectedValue={selectedScore?.value ?? null}
                    isMobileView={false}
                    sortItemsByValue={false}
                    inputHeight={isEncompass ? 35 : undefined}
                    selectFontSize={12}
                    optionsFontSize={12}
                    selectIcon={isEncompass ? 'caret' : 'angle'}
                />
            </div>
            <div>
                {isClearMode ? (
                    <VPButton variant="filled" text="Clear Score" disabled={isDisabled || isSubmitting} onClick={handleClear} />
                ) : (
                    <VPButton variant="filled" text="Save Score" disabled={isDisabled || !selectedScore} onClick={handleSave} />
                )}
            </div>
        </div>
    );
};

const ScoreVideoEvent = VideoEventService.inject(_ScoreVideoEvent);

export default ScoreVideoEvent;
